import React from 'react';
import { CheckCircle, Home, FileText } from 'lucide-react';
import { useApp } from '@/context/AppContext';
import { formatINR } from '@/data/mockData';

export default function UploadSuccessScreen() {
  const { navigate, screenParams, completionPct, pendingCount } = useApp();
  const { invoice, ocrData } = screenParams;

  return (
    <div className="flex flex-col min-h-screen bg-[#F5F6FA] pb-6">
      {/* Success hero */}
      <div className="bg-gradient-to-b from-[#1E2744] to-[#2563EB] px-4 pt-12 pb-16 flex flex-col items-center text-center">
        <div className="relative mb-4">
          <div className="w-20 h-20 bg-green-400/20 rounded-full flex items-center justify-center">
            <CheckCircle size={48} color="#4ADE80" strokeWidth={1.5} />
          </div>
          <div className="absolute -top-1 -right-1 w-6 h-6 bg-green-400 rounded-full flex items-center justify-center">
            <span className="text-white text-xs font-black">✓</span>
          </div>
        </div>
        <h1 className="text-white font-black text-xl mb-1">Invoice Uploaded!</h1>
        <p className="text-white/70 text-sm">Successfully submitted for verification</p>
        <div className="mt-3 bg-blue-400/30 rounded-xl px-4 py-2 flex items-center gap-2">
          <div className="w-2 h-2 bg-blue-300 rounded-full animate-pulse" />
          <span className="text-blue-200 text-xs font-semibold">Status: Under Review</span>
        </div>
      </div>

      <div className="px-4 -mt-6 flex flex-col gap-4">
        {/* Summary card */}
        <div className="bg-white rounded-2xl p-5 shadow-lg slide-up">
          <div className="text-xs font-black text-slate-500 uppercase tracking-widest mb-4">Submission Summary</div>
          <div className="flex flex-col gap-3">
            <div className="flex justify-between items-center py-2 border-b border-slate-100">
              <span className="text-xs text-slate-400 font-semibold uppercase">Invoice No</span>
              <span className="font-bold text-[#1E2744] text-sm">{invoice?.invoiceNo || ocrData?.invoiceNo}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-slate-100">
              <span className="text-xs text-slate-400 font-semibold uppercase">Invoice Date</span>
              <span className="font-semibold text-slate-700 text-sm">{invoice?.date || ocrData?.invoiceDate}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-slate-100">
              <span className="text-xs text-slate-400 font-semibold uppercase">Buyer Name</span>
              <span className="font-semibold text-slate-700 text-sm">{invoice?.buyer || ocrData?.buyerName}</span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-xs text-slate-400 font-semibold uppercase">Total Value</span>
              <span className="rupee-value font-black text-[#1E2744] text-base">₹{formatINR(invoice?.amount || ocrData?.totalAmount || 0)}</span>
            </div>
          </div>
        </div>

        {/* Updated stats */}
        <div className="bg-[#1E2744] rounded-2xl p-4 slide-up-d1">
          <div className="text-xs font-black text-white/60 uppercase tracking-widest mb-3">Updated Status</div>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white/10 rounded-xl p-3">
              <div className="text-white/60 text-[10px] font-semibold uppercase">Upload Complete</div>
              <div className="rupee-value text-white font-black text-xl mt-0.5">{completionPct}%</div>
            </div>
            <div className="bg-white/10 rounded-xl p-3">
              <div className="text-white/60 text-[10px] font-semibold uppercase">Still Pending</div>
              <div className="rupee-value text-[#FF7043] font-black text-base mt-0.5">{pendingCount} inv.</div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2 slide-up-d2">
          <button
            data-testid="success-back-home-btn"
            onClick={() => navigate('HOME')}
            className="w-full bg-[#2563EB] text-white rounded-xl py-4 font-bold text-sm tracking-wide tap-scale flex items-center justify-center gap-2"
          >
            <Home size={18} />
            Back to Home
          </button>
          <button
            data-testid="success-view-summary-btn"
            onClick={() => navigate('INVOICES')}
            className="w-full border-2 border-[#1E2744] text-[#1E2744] rounded-xl py-3.5 font-bold text-sm tap-scale flex items-center justify-center gap-2"
          >
            <FileText size={18} />
            View Invoice Summary
          </button>
        </div>
      </div>
    </div>
  );
}
